import { ChangeEvent, useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ThemeProvider } from 'styled-components';
import Theme from 'styles/theme';

// API
import { getNoticeList, getNotice, createNotice, updateNotice, deleteNotice, toggleNoticeVisibility } from 'components/form/api/admin.api';
import { NoticeItem, CreateNoticeInput } from 'components/form/interfaces/admin.interface';

// atomic components
import Container from 'components/UI/atoms/Container';
import Grid from 'components/UI/atoms/Grid';
import Button from 'components/UI/atoms/Button';
import TextField from 'components/UI/atoms/TextField';
import Table from 'components/UI/atoms/Table';
import Select from 'components/UI/atoms/Select';
import Pagination from 'components/UI/molecules/Pagination';

// icons
import { getFormatDate } from 'common/common.function';
import { INoticeBoard, NoticeFormData } from './interfaces/admin.interface';
import { WakeUp } from 'common/interface/props';

// styles
import {
  MySwal,
  ButtonTag,
  ModalContainer,
  EditorSection,
  PreviewSection,
  FormGroup,
  Label,
  StyledTextField,
  StyledTextArea,
  CheckboxContainer,
  PreviewContainer,
  PreviewHeaderSection,
  PreviewContentSection,
  PreviewFooterSection,
  PreviewTopRightBox,
  PreviewCheckbox,
  PreviewLabel,
  PreviewTitle,
  PreviewSubtitle,
  PreviewParagraph,
  PreviewHighlightBox,
  PreviewHighlightItem,
  PreviewHighlightItemLabel,
  PreviewHighlightItemValue,
  PreviewSpacer,
  PreviewLink,
} from 'components/admin/styles/NotcieBoard.style';

/**
 * @alias 공지관리 컴포넌트
 * @Param solType 솔루션 타입
 * @Param isIntegrated 통합 관리자 여부
 */
export default function NoticeBoard({ solType, isIntegrated }: INoticeBoard) {
  // localStorage에서 userInfo를 안전하게 파싱
  const getUserId = (): number => {
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (!userInfoStr) return 0;
      
      const userInfo = JSON.parse(userInfoStr);
      return userInfo?.userId || 0;
    } catch (error) {
      // JSON 파싱 실패 시 (JWT 토큰이거나 잘못된 형식인 경우)
      console.warn('userInfo 파싱 실패:', error);
      return 0;
    }
  };
  
  const userId: number = getUserId();
  // 검색 관련 state
  const [searchSolType, setSearchSolType] = useState(isIntegrated ? 'ALL' : solType);
  const [searchType, setSearchType] = useState<'title' | 'subtitle' | 'content' | 'all'>('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 0);
    return d;
  });
  const [visibleYn, setVisibleYn] = useState<boolean | undefined>(undefined);

  // 실제 검색에 적용되는 state (검색 버튼 클릭 시에만 업데이트)
  const [appliedSearchType, setAppliedSearchType] = useState<'title' | 'subtitle' | 'content' | 'all'>('all');
  const [appliedSearchKeyword, setAppliedSearchKeyword] = useState('');

  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // 공지사항 목록 조회
  const { data: noticeListData, refetch } = useQuery({
    queryKey: ['noticeList', searchSolType, appliedSearchType, appliedSearchKeyword, startDate, endDate, visibleYn, currentPage, pageSize],
    queryFn: () =>
      getNoticeList({
        solType: isIntegrated ? searchSolType : solType,
        includeAll: !isIntegrated,
        searchType: appliedSearchKeyword ? appliedSearchType : 'all',
        searchKeyword: appliedSearchKeyword || undefined,
        createdAtFrom: getFormatDate(startDate),
        createdAtTo: getFormatDate(endDate),
        visibleYn,
        page: currentPage,
        pageSize,
      }),
    enabled: true,
    keepPreviousData: true,
  });

  // 검색 버튼 클릭 시 실행
  const handleSearch = () => {
    setAppliedSearchType(searchType);
    setAppliedSearchKeyword(searchKeyword);
    setCurrentPage(1);
  };

  // 공지 생성 mutation
  const createNoticeMutation = useMutation(createNotice, {
    onSuccess: () => {
      MySwal.fire({
        title: '성공',
        text: '공지사항이 생성되었습니다.',
        icon: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      MySwal.fire({
        title: '실패',
        text: error.message || '공지사항 생성에 실패했습니다.',
        icon: 'error',
      });
    },
  });

  // 공지 수정 mutation
  const updateNoticeMutation = useMutation(updateNotice, {
    onSuccess: () => {
      MySwal.fire({
        title: '성공',
        text: '공지사항이 수정되었습니다.',
        icon: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      MySwal.fire({
        title: '실패',
        text: error.message || '공지사항 수정에 실패했습니다.',
        icon: 'error',
      });
    },
  });

  // 공지 삭제 mutation
  const deleteNoticeMutation = useMutation(deleteNotice, {
    onSuccess: () => {
      MySwal.fire({
        title: '성공',
        text: '공지사항이 삭제되었습니다.',
        icon: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      MySwal.fire({
        title: '실패',
        text: error.message || '공지사항 삭제에 실패했습니다.',
        icon: 'error',
      });
    },
  });

  // 공지 노출<-> 비노출 Mutation
  const toggleNoticeVisibilityMutation = useMutation(toggleNoticeVisibility, {
    onSuccess: () => {
      MySwal.fire({
        title: '성공',
        text: '공지사항 노출 여부가 변경되었습니다.',
        icon: 'success',
      });
      refetch();
    },
    onError: (error: any) => {
      MySwal.fire({
        title: '실패',
        text: error.message || '공지사항 노출 여부 변경에 실패했습니다.',
        icon: 'error',
      });
    },
  });

  // 공지 생성 팝업
  const openCreateNoticeModal = () => {
    showNoticeFormModal(undefined);
  };

  // 공지 노출<->비노출 토글
  const handleToggleNoticeVisibility = async (notice: NoticeItem) => {
    const result = await MySwal.fire({
      title: '노출 여부 변경',
      html: `정말로 이 공지사항을 ${notice.visibleYn ? '비노출' : '노출'}하시겠습니까?<br/>※ 노출 여부 변경 후 실서버에 즉시 반영됩니다.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '확인',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      toggleNoticeVisibilityMutation.mutate(notice.noticeId);
    }
  };

  // 공지 상세/수정 팝업
  const openNoticeDetailModal = async (noticeId: number) => {
    try {
      const response = await getNotice(noticeId);
      if (response.success && response.notice) {
        showNoticeFormModal(response.notice);
      } else {
        MySwal.fire({
          title: '실패',
          text: '공지사항을 불러오는데 실패했습니다.',
          icon: 'error',
        });
      }
    } catch (error: any) {
      MySwal.fire({
        title: '실패',
        text: error.message || '공지사항을 불러오는데 실패했습니다.',
        icon: 'error',
      });
    }
  };

  // 공지 삭제
  const handleDeleteNotice = async (noticeId: number, formData?: NoticeFormData, notice?: NoticeItem) => {
    const result = await MySwal.fire({
      title: '삭제 확인',
      html: `정말로 이 공지사항을 삭제하시겠습니까?<br/>※ 삭제 후 복구할 수 없으며, 실서버에 즉시 반영됩니다.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    });

    if (result.isConfirmed) {
      deleteNoticeMutation.mutate(noticeId);
    } else if (formData && notice) {
      // 취소 시 폼 모달 다시 열기 (작성한 데이터 유지)
      showNoticeFormModal(notice, formData);
    }
  };

  // 임시저장 핸들러
  const handleTempSave = async (data: NoticeFormData, noticeId?: number) => {
    const isEditMode = !!noticeId;

    // 폼 모달 닫기
    MySwal.close();

    // 확인 알림
    const confirmResult = await MySwal.fire({
      title: '임시저장 확인',
      html: `공지사항을 비노출 상태로 임시저장하시겠습니까?<br/>사용자 화면에서 미리보기를 확인할 수 있습니다.<br/>※ 확인 후 공지 상태를 노출로 변경하면 실서버에 즉시 반영됩니다.`,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: '임시저장',
      cancelButtonText: '취소',
    });

    // 취소하면 폼 모달 다시 열기 (작성한 데이터 유지)
    if (!confirmResult.isConfirmed) {
      const notice = noticeId ? await getNotice(noticeId).then((res) => (res.success && res.notice ? res.notice : undefined)) : undefined;
      showNoticeFormModal(notice, data);
      return;
    }

    const payload = buildPayload(data, { visibleYn: false }); // 임시저장은 비노출

    try {
      let savedNoticeId: number;

      if (isEditMode && noticeId) {
        // 수정 모드
        await updateNotice({ noticeId, ...payload, userId: userId });
        savedNoticeId = noticeId;
      } else {
        // 신규 생성
        const response = await createNotice({ ...payload, userId: userId } as CreateNoticeInput);
        if (!response.notice?.noticeId) {
          throw new Error('공지사항 ID를 받아오지 못했습니다.');
        }
        savedNoticeId = response.notice.noticeId;
      }

      // 미리보기 URL
      const url = `${process.env.REACT_APP_FRONTEND_ENDPOINT}/${data.solType}/member/login?preview=true&noticeId=${savedNoticeId}`;

      // 성공 알림 표시 후 새 창 열기
      await MySwal.fire({
        title: '임시저장 완료',
        html: '잠시 후 미리보기 페이지가 열립니다.',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
      });

      // 시간차를 두고 새 창 열기
      setTimeout(() => {
        window.open(url, '_blank');
      }, 300);
    } catch (error: any) {
      MySwal.fire({
        title: '실패',
        text: error.message || '임시저장에 실패했습니다.',
        icon: 'error',
      });
    }
  };

  // 공지 폼 모달
  const showNoticeFormModal = (notice?: NoticeItem, preservedFormData?: NoticeFormData) => {
    const isEditMode = !!notice;

    // 기본 순서 계산: 수정 모드일 때는 기존 order, 새로 만들 때는 최대 order + 1
    const calculateDefaultOrder = () => {
      // 수정 모드일 때는 기존 order 값 사용
      if (isEditMode && notice !== undefined) {
        return notice.order;
      }
      // preservedFormData가 있으면 그 값 사용
      if (preservedFormData !== undefined) {
        return preservedFormData.order;
      }

      // 새로 만들 때만 최대값 + 1 계산
      const maxOrder = (noticeListData?.notices || []).reduce((max, item) => {
        const itemOrder = item.order;
        // null이 아닌 숫자만 비교
        if (itemOrder === null) return max;
        return itemOrder > max ? itemOrder : max;
      }, 0);

      return maxOrder + 1;
    };

    MySwal.fire({
      title: isEditMode ? '공지사항 수정' : '공지사항 생성',
      html: (
        <ThemeProvider theme={Theme}>
          <NoticeFormModal
            notice={notice}
            solType={searchSolType}
            isIntegrated={isIntegrated ?? false}
            onSubmit={handleFormSubmit}
            onTempSave={handleTempSave}
            onDelete={handleDeleteNotice}
            preservedFormData={preservedFormData}
            defaultOrder={calculateDefaultOrder()}
          />
        </ThemeProvider>
      ),
      width: '90%',
      showConfirmButton: false,
      showCloseButton: true,
      customClass: {
        popup: 'swal2-no-padding',
      },
    });
  };

  // Payload 생성 공통 함수
  const buildPayload = (data: NoticeFormData, options?: { visibleYn?: boolean }) => {
    // content를 배열로 변환 (줄바꿈 기준, 빈 줄은 /n로 표시)
    const contentArray = data.content.split('\n').map((line) => (line.trim() === '' ? '/n' : line));

    // infoBox 구성 - 실제 내용이 있는 항목만 필터링
    const filteredItems = data.infoBoxItems.filter((item) => item.label && item.label.trim()).map((item) => ({ label: item.label, value: item.value || '' }));
    const filteredWarning =
      data.infoBoxWarning && data.infoBoxWarning.trim() ? data.infoBoxWarning.split('\n').map((line) => (line.trim() === '' ? '/n' : line)) : undefined;
    const filteredLinks = data.infoBoxLinks.filter((link) => link.text && link.text.trim() && link.url && link.url.trim());

    // 실제 내용이 하나라도 있을 때만 infoBox 생성, 모두 비어있으면 null
    const infoBox =
      filteredItems.length > 0 || filteredWarning || filteredLinks.length > 0
        ? {
            items: filteredItems,
            warning: filteredWarning,
            links: filteredLinks,
          }
        : null;

    // 시작일과 종료일에 초 설정
    const startDateWithSeconds = new Date(data.startDate);
    startDateWithSeconds.setSeconds(0); // 시작일은 00초

    const endDateWithSeconds = new Date(data.endDate);
    endDateWithSeconds.setSeconds(59); // 종료일은 59초

    // 차단 시작일과 종료일에 초 설정 (URGENT 타입일 때만)
    let blockStartDateWithSeconds: string | undefined;
    let blockEndDateWithSeconds: string | undefined;

    if (data.noticeType === 'URGENT') {
      if (data.blockStartDate) {
        const blockStart = new Date(data.blockStartDate);
        blockStart.setSeconds(0);
        blockStartDateWithSeconds = blockStart.toISOString();
      }

      if (data.blockEndDate) {
        const blockEnd = new Date(data.blockEndDate);
        blockEnd.setSeconds(59);
        blockEndDateWithSeconds = blockEnd.toISOString();
      }
    }

    // URGENT 타입일 때만 blockStartDate와 blockEndDate를 포함 (값이 있을 때만)
    const blockDates: { blockStartDate?: string; blockEndDate?: string } = {};
    if (data.noticeType === 'URGENT') {
      if (blockStartDateWithSeconds) {
        blockDates.blockStartDate = blockStartDateWithSeconds;
      }
      if (blockEndDateWithSeconds) {
        blockDates.blockEndDate = blockEndDateWithSeconds;
      }
    }

    return {
      title: data.title,
      subtitle: data.subtitle, // 빈 문자열도 허용
      content: contentArray.length === 1 ? contentArray[0] : contentArray,
      infoBox,
      startDate: startDateWithSeconds.toISOString(),
      endDate: endDateWithSeconds.toISOString(),
      ...(data.noticeType === 'URGENT' ? blockDates : {}),
      visibleYn: options?.visibleYn !== undefined ? options.visibleYn : data.visibleYn,
      solType: data.solType,
      noticeType: data.noticeType,
      order: data.order,
    };
  };

  // 폼 제출 핸들러
  const handleFormSubmit = async (data: NoticeFormData, noticeId?: number) => {
    const isEditMode = !!noticeId;

    // 폼 모달 닫기
    MySwal.close();

    // 확인 알림
    const confirmResult = await MySwal.fire({
      title: isEditMode ? '공지사항 수정 확인' : '공지사항 생성 확인',
      html: `${isEditMode ? '이 공지사항을 수정하시겠습니까?' : '새 공지사항을 생성하시겠습니까?'}<br/>※ 실서버에 즉시 반영됩니다.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: isEditMode ? '수정' : '생성',
      cancelButtonText: '취소',
    });

    // 취소하면 폼 모달 다시 열기 (입력한 데이터 유지)
    if (!confirmResult.isConfirmed) {
      const notice = noticeId ? await getNotice(noticeId).then((res) => (res.success && res.notice ? res.notice : undefined)) : undefined;
      showNoticeFormModal(notice, data);
      return;
    }

    // 생성 및 수정 payload
    const payload = buildPayload(data);

    if (isEditMode && noticeId) {
      // 수정
      updateNoticeMutation.mutate({ noticeId, ...payload, userId: userId });
    } else {
      // 생성
      createNoticeMutation.mutate({ ...payload, userId: userId } as CreateNoticeInput);
    }
  };

  return (
    <Container noBody={true}>
      <Grid top={20}>
        <Grid display="flex" direction="row" alignItems="center" justifyContent="flex-end">
          <Button color="main" size="small" variant="outlined" width={300} height={40} borderRadius={4} onClick={openCreateNoticeModal}>
            공지사항 작성
          </Button>
        </Grid>

        {/* 검색 영역 */}
        <Grid display="flex" direction="row" alignItems="center" top={20} bottom={20} gap={10}>
          <Grid display="inline-flex" direction="row" alignItems="center" justifyContent="flex-start" gap={10}>
            <DatePicker
              locale="ko"
              selected={startDate}
              maxDate={endDate}
              onChange={(date: Date) => {
                const newDate = new Date(date);
                newDate.setHours(0, 0, 0, 0);
                setStartDate(newDate);
                setCurrentPage(1);
              }}
              dateFormat="yyyy-MM-dd"
              customInput={<TextField variant="outlined" width={120} style={{ fontSize: '0.88rem' }} />}
              popperClassName="date-picker-popper"
              popperProps={{
                strategy: 'fixed',
                style: { zIndex: 9999 },
              }}
            />
            <span>~</span>
            <DatePicker
              locale="ko"
              selected={endDate}
              minDate={startDate}
              onChange={(date: Date) => {
                const newDate = new Date(date);
                newDate.setHours(23, 59, 59, 0);
                setEndDate(newDate);
                setCurrentPage(1);
              }}
              dateFormat="yyyy-MM-dd"
              customInput={<TextField variant="outlined" width={120} style={{ fontSize: '0.88rem' }} />}
              popperClassName="date-picker-popper"
              popperProps={{
                strategy: 'fixed',
                style: { zIndex: 9999 },
              }}
            />
          </Grid>

          <Grid display="flex" direction="row" alignItems="center" justifyContent="flex-end" gap={10}>
            {isIntegrated && (
              <Select
                value={searchSolType}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  setSearchSolType(e.target.value as WakeUp);
                  setCurrentPage(1);
                }}
                variant="outlined"
                style={{ fontSize: '0.88rem' }}
              >
                <option value="ALL">전체</option>
                <option value="COUPANG">쿠팡</option>
                <option value="ST11">11번가</option>
                <option value="ALWAYZ">올웨이즈</option>
                <option value="KAKAO">카카오</option>
                <option value="ESM">ESM+</option>
                <option value="TOSS">TOSS</option>
              </Select>
            )}

            <Select
              value={visibleYn === undefined ? 'all' : visibleYn ? 'true' : 'false'}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                setVisibleYn(e.target.value === 'all' ? undefined : e.target.value === 'true');
                setCurrentPage(1);
              }}
              variant="outlined"
              style={{ fontSize: '0.88rem' }}
            >
              <option value="all">전체</option>
              <option value="true">노출</option>
              <option value="false">비노출</option>
            </Select>

            <Select
              value={searchType}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                const newType = e.target.value as 'title' | 'subtitle' | 'content' | 'all';
                setSearchType(newType);
                // 키워드가 있으면 즉시 검색
                if (searchKeyword.trim()) {
                  setAppliedSearchType(newType);
                  setAppliedSearchKeyword(searchKeyword);
                  setCurrentPage(1);
                }
              }}
              variant="outlined"
              style={{ fontSize: '0.88rem' }}
            >
              <option value="all">전체</option>
              <option value="title">제목</option>
              <option value="subtitle">부제목</option>
              <option value="content">내용</option>
            </Select>

            <TextField
              type="text"
              placeholder="검색어 입력"
              variant="outlined"
              value={searchKeyword}
              width={300}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              style={{ fontSize: '0.88rem' }}
            />

            <Button color="main" width={80} size="small" borderRadius={5} onClick={handleSearch}>
              검색
            </Button>
          </Grid>
        </Grid>

        {/* 테이블 */}
        <Grid>
          <Table
            headerItems={[
              { width: 80, flex: 0.8, headerName: 'No.', align: 'center', headerAlign: 'center', sortable: false },
              { width: 200, flex: 2, headerName: '제목', align: 'left', headerAlign: 'center', sortable: false },
              { width: 180, flex: 1.8, headerName: '시작일', align: 'center', headerAlign: 'center', sortable: false },
              { width: 180, flex: 1.8, headerName: '종료일', align: 'center', headerAlign: 'center', sortable: false },
              {
                width: 120,
                flex: 1.2,
                headerName: '노출여부',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => {
                  const status = params.value;
                  const color = status === '노출중' ? '#1BAB66' : 'inherit';
                  return <span style={{ color }}>{status}</span>;
                },
              },
              {
                width: 100,
                flex: 1,
                headerName: '솔루션',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => {
                  switch (params.value) {
                    case 'COUPANG':
                      return '쿠팡';
                    case 'ST11':
                      return '11번가';
                    case 'ALWAYZ':
                      return '올웨이즈';
                    case 'KAKAO':
                      return '카카오';
                    case 'ESM':
                    case 'ESM_ALL':
                      return 'ESM+';
                    case 'TOSS':
                      return '토스';
                    case 'ALL':
                      return '전체';
                    default:
                      return params.value || '-';
                  }
                },
              },
              {
                width: 100,
                flex: 1,
                headerName: '타입',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => {
                  switch (params.value) {
                    case 'INFORMATION':
                      return '정보 공지';
                    case 'URGENT':
                      return '긴급 공지';
                    default:
                      return params.value || '-';
                  }
                },
              },
              { width: 100, flex: 1, headerName: '순서', align: 'center', headerAlign: 'center', sortable: true },
              { width: 200, flex: 2, headerName: '수정일', align: 'center', headerAlign: 'center', sortable: false },
              { width: 100, flex: 1, headerName: '작성자', align: 'center', headerAlign: 'center', sortable: false },
              {
                width: 180,
                flex: 1.8,
                headerName: '관리',
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params: any) => (
                  <Grid display="flex" gap={5} justifyContent="center">
                    <ButtonTag color="main" size="small" variant="outlined" onClick={() => openNoticeDetailModal(params.value.noticeId)}>
                      상세
                    </ButtonTag>
                    <ButtonTag color="main" size="small" variant="outlined" onClick={() => handleToggleNoticeVisibility(params.value)}>
                      {params.value.visibleYn ? '비노출' : '노출'}
                    </ButtonTag>
                    <ButtonTag color="main" size="small" variant="outlined" onClick={() => handleDeleteNotice(params.value.noticeId)}>
                      삭제
                    </ButtonTag>
                  </Grid>
                ),
              },
            ]}
            bodyItems={
              noticeListData?.notices?.map((notice, index) => {
                const formatDateTime = (dateStr: string, includeSeconds: boolean = true) => {
                  const date = new Date(dateStr);
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  const seconds = String(date.getSeconds()).padStart(2, '0');
                  return includeSeconds ? `${year}-${month}-${day} ${hours}:${minutes}:${seconds}` : `${year}-${month}-${day} ${hours}:${minutes}`;
                };

                // 페이지네이션을 고려한 역순 행 번호 계산 (최신 데이터가 큰 번호)
                const totalCount = noticeListData?.totalCount || 0;
                const rowNumber = totalCount - ((currentPage - 1) * pageSize + index);

                // 노출 상태 계산
                const getVisibilityStatus = () => {
                  if (!notice.visibleYn) {
                    return '숨김';
                  }
                  // visibleYn이 true일 때 종료일 체크
                  const endDate = new Date(notice.endDate);
                  const now = new Date();
                  if (now > endDate) {
                    return '기간만료';
                  }
                  return '노출중';
                };

                return [
                  rowNumber,
                  notice.title,
                  formatDateTime(notice.startDate, false),
                  formatDateTime(notice.endDate, false),
                  getVisibilityStatus(),
                  notice.solType,
                  notice.noticeType,
                  notice.order || 0,
                  formatDateTime(notice.updatedAt),
                  notice.authorId,
                  notice,
                ];
              }) || []
            }
          />
          <Grid top={30} />
          <Pagination nowPage={currentPage} max={noticeListData?.totalPage || 1} size={5} nowPageEvent={setCurrentPage} />
        </Grid>
      </Grid>
    </Container>
  );
}

/**
 * @alias 공지사항 폼 모달 컴포넌트
 */
function NoticeFormModal({
  notice,
  solType,
  onSubmit,
  onTempSave,
  onDelete,
  isIntegrated,
  preservedFormData,
  defaultOrder,
}: {
  notice?: NoticeItem;
  solType: string;
  onSubmit: (data: NoticeFormData, noticeId?: number) => void;
  onTempSave: (data: NoticeFormData, noticeId?: number) => void;
  onDelete: (noticeId: number, formData?: NoticeFormData, notice?: NoticeItem) => void;
  isIntegrated: boolean;
  preservedFormData?: NoticeFormData;
  defaultOrder: number | null;
}) {
  const isEditMode = !!notice;
  const [hasWarning, setHasWarning] = useState<boolean>(preservedFormData ? !!preservedFormData.infoBoxWarning : !!notice?.infoBox?.warning);
  const [isEndDateUndefined, setIsEndDateUndefined] = useState<boolean>(() => {
    if (preservedFormData) {
      const year = new Date(preservedFormData.endDate).getFullYear();
      return year === 2999;
    }
    if (notice?.endDate) {
      const year = new Date(notice.endDate).getFullYear();
      return year === 2999;
    }
    return false;
  });
  const [isBlockEndDateUndefined, setIsBlockEndDateUndefined] = useState<boolean>(() => {
    if (preservedFormData) {
      if (preservedFormData.blockEndDate) {
        const year = new Date(preservedFormData.blockEndDate).getFullYear();
        return year === 2999;
      }
      return false;
    }
    if (notice?.blockEndDate) {
      const year = new Date(notice.blockEndDate).getFullYear();
      return year === 2999;
    }
    return false;
  });

  // React Hook Form 초기화
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<NoticeFormData>({
    defaultValues: preservedFormData
      ? (() => {
          const startDate = preservedFormData.startDate ? new Date(preservedFormData.startDate) : new Date();
          const endDate = preservedFormData.endDate ? new Date(preservedFormData.endDate) : new Date();
          return {
            ...preservedFormData,
            startDate,
            endDate,
            blockStartDate: preservedFormData.blockStartDate
              ? new Date(preservedFormData.blockStartDate)
              : preservedFormData.noticeType === 'URGENT'
              ? new Date(startDate)
              : undefined,
            blockEndDate: preservedFormData.blockEndDate
              ? new Date(preservedFormData.blockEndDate)
              : preservedFormData.noticeType === 'URGENT'
              ? new Date(endDate)
              : undefined,
          };
        })()
      : (() => {
          const startDate = notice?.startDate ? new Date(notice.startDate) : new Date();
          const endDate = notice?.endDate ? new Date(notice.endDate) : new Date();
          return {
            title: notice?.title || '',
            subtitle: notice?.subtitle || '',
            content: Array.isArray(notice?.content) ? notice?.content.map((line) => (line === '/n' ? '' : line)).join('\n') : notice?.content || '',
            infoBoxItems: notice?.infoBox?.items || [],
            infoBoxWarning: Array.isArray(notice?.infoBox?.warning)
              ? notice?.infoBox?.warning.map((line) => (line === '/n' ? '' : line)).join('\n')
              : notice?.infoBox?.warning || '',
            infoBoxLinks: notice?.infoBox?.links || [],
            startDate,
            endDate,
            blockStartDate: notice?.blockStartDate
              ? new Date(notice.blockStartDate)
              : notice?.noticeType === 'URGENT'
              ? new Date(startDate)
              : undefined,
            blockEndDate: notice?.blockEndDate
              ? new Date(notice.blockEndDate)
              : notice?.noticeType === 'URGENT'
              ? new Date(endDate)
              : undefined,
            visibleYn: notice?.visibleYn ?? true,
            solType: notice?.solType || (isIntegrated ? 'ALL' : solType),
            noticeType: notice?.noticeType || 'INFORMATION',
            order: defaultOrder || 1,
          };
        })(),
  });

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: 'infoBoxItems',
  });

  const {
    fields: linkFields,
    append: appendLink,
    remove: removeLink,
  } = useFieldArray({
    control,
    name: 'infoBoxLinks',
  });

  // 폼 데이터 watch (미리보기용)
  const formData = watch();

  // 공지 타입이 URGENT로 변경될 때 차단 시작일/종료일을 공지 시작일/종료일로 자동 설정
  useEffect(() => {
    if (formData.noticeType === 'URGENT') {
      if (!formData.blockStartDate && formData.startDate) {
        setValue('blockStartDate', new Date(formData.startDate));
      }
      if (!formData.blockEndDate && formData.endDate) {
        setValue('blockEndDate', new Date(formData.endDate));
      }
    }
  }, [formData.noticeType, formData.startDate, formData.endDate, formData.blockStartDate, formData.blockEndDate, setValue]);

  // 미리보기 데이터 구성
  const hasInfoBoxContent =
    (formData.infoBoxItems && formData.infoBoxItems.length > 0) || // 필드가 추가되면 미리보기에 표시
    hasWarning || // 경고/안내 메시지 필드가 추가되면 미리보기에 표시
    (formData.infoBoxLinks && formData.infoBoxLinks.length > 0); // 링크 필드가 추가되면 미리보기에 표시

  const previewData = {
    title: formData.title || '[제목]',
    subtitle: formData.subtitle,
    content: formData.content ? formData.content.split('\n').map((line) => (line.trim() === '' ? '/n' : line)) : ['[내용]'],
    infoBox: hasInfoBoxContent
      ? {
          items:
            formData.infoBoxItems?.map((item) => ({
              label: item.label || '[요약 라벨]',
              value: item.value || '[요약 내용]',
            })) || [],
          warning:
            hasWarning && (!formData.infoBoxWarning || !formData.infoBoxWarning.trim())
              ? ['[경고/안내 메시지를 입력하세요]']
              : formData.infoBoxWarning
              ? formData.infoBoxWarning.split('\n').map((line) => (line.trim() === '' ? '/n' : line))
              : undefined,
          links:
            formData.infoBoxLinks?.map((link) => ({
              text: link.text || '[링크 텍스트]',
              url: link.url || '',
            })) || [],
        }
      : undefined,
  };

  /**
   * @alias 공지사항 게시 핸들러
   */
  const handleFormSubmit = (data: NoticeFormData) => {
    // 종료일이 미정이면 2999-12-31로 설정
    if (isEndDateUndefined) {
      data.endDate = new Date('2999-12-31T23:59:59');
    }
    // 차단 종료일이 미정이면 2999-12-31로 설정 (URGENT 타입일 때만)
    if (data.noticeType === 'URGENT' && isBlockEndDateUndefined) {
      data.blockEndDate = new Date('2999-12-31T23:59:59');
    }
    onSubmit(data, notice?.noticeId);
  };

  /**
   * @alias 공지사항 임시저장 핸들러
   * @description 노출 여부를 false 로 저장 후 사용자 화면에서 미리보기
   */
  const handleTempSaveClick = (data: NoticeFormData) => {
    // 종료일이 미정이면 2999-12-31로 설정
    if (isEndDateUndefined) {
      data.endDate = new Date('2999-12-31T23:59:59');
    }
    // 차단 종료일이 미정이면 2999-12-31로 설정 (URGENT 타입일 때만)
    if (data.noticeType === 'URGENT' && isBlockEndDateUndefined) {
      data.blockEndDate = new Date('2999-12-31T23:59:59');
    }

    onTempSave(data, notice?.noticeId);
  };

  return (
    <ModalContainer>
      {/* 왼쪽: 편집 영역 */}
      <EditorSection>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <FormGroup>
            <Label>제목 *</Label>
            <StyledTextField {...register('title', { required: '제목은 필수입니다.' })} placeholder="공지사항 제목" />
            {errors.title && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.title.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label>부제목</Label>
            <StyledTextField {...register('subtitle')} placeholder="공지사항 부제목" />
          </FormGroup>

          <FormGroup>
            <Label>내용 * (줄바꿈으로 문단 구분)</Label>
            <StyledTextArea {...register('content', { required: '내용은 필수입니다.' })} placeholder="공지사항 내용을 입력하세요" />
            {errors.content && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.content.message}</div>}
          </FormGroup>

          <FormGroup>
            <Label>정보 박스 - 요약</Label>
            {itemFields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e2e2e2', borderRadius: '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <StyledTextField {...register(`infoBoxItems.${index}.label` as const)} placeholder="라벨 (예: 중단 기간)" />
                  <div>
                    <StyledTextField
                      {...register(`infoBoxItems.${index}.value` as const, {
                        validate: (value, formValues) => {
                          const label = formValues.infoBoxItems?.[index]?.label;
                          if (label && label.trim() !== '' && (!value || value.trim() === '')) {
                            return '라벨이 있으면 내용은 필수입니다.';
                          }
                          return true;
                        },
                      })}
                      placeholder="내용 (예: 2025년 7월 4일 00시 ~ 미정)"
                    />
                    {errors.infoBoxItems?.[index]?.value && (
                      <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', textAlign: 'left' }}>{errors.infoBoxItems[index]?.value?.message}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="button" onClick={() => removeItem(index)} size="small" color="main" variant="outlined">
                      삭제
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" onClick={() => appendItem({ label: '', value: '' })} size="small" color="main" variant="outlined">
              + 요약 추가
            </Button>
          </FormGroup>

          <FormGroup>
            <Label>정보 박스 - 경고/안내 메시지</Label>
            {hasWarning ? (
              <div style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e2e2e2', borderRadius: '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <StyledTextArea {...register('infoBoxWarning')} placeholder="경고/안내 메시지 (줄바꿈으로 문단 구분)" rows={5} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="button"
                      onClick={() => {
                        setHasWarning(false);
                        setValue('infoBoxWarning', '');
                      }}
                      size="small"
                      color="main"
                      variant="outlined"
                    >
                      삭제
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button type="button" onClick={() => setHasWarning(true)} size="small" color="main" variant="outlined">
                + 경고/안내 메시지 추가
              </Button>
            )}
          </FormGroup>

          <FormGroup>
            <Label>정보 박스 - 링크</Label>
            {linkFields.map((field, index) => (
              <div key={field.id} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e2e2e2', borderRadius: '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <StyledTextField {...register(`infoBoxLinks.${index}.text` as const)} placeholder="링크 텍스트" />
                  <div>
                    <StyledTextField
                      {...register(`infoBoxLinks.${index}.url` as const, {
                        validate: (value) => {
                          if (!value || value.trim() === '') return true; // 빈 값은 허용
                          const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
                          return urlPattern.test(value) || 'URL 형식이 올바르지 않습니다. (예: https://example.com)';
                        },
                      })}
                      placeholder="URL (예: https://example.com)"
                    />
                    {errors.infoBoxLinks?.[index]?.url && (
                      <div style={{ color: 'red', fontSize: '12px', marginTop: '4px', textAlign: 'left' }}>{errors.infoBoxLinks[index]?.url?.message}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="button" onClick={() => removeLink(index)} size="small" color="main" variant="outlined">
                      삭제
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" onClick={() => appendLink({ text: '', url: '' })} size="small" color="main" variant="outlined">
              + 링크 추가
            </Button>
          </FormGroup>

          <FormGroup>
            <Label>시작일 *</Label>

            <Controller
              control={control}
              name="startDate"
              rules={{ required: '시작일은 필수입니다.' }}
              render={({ field }) => (
                <DatePicker
                  locale="ko"
                  selected={field.value}
                  onChange={(date) => field.onChange(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  dateFormat="yyyy-MM-dd HH:mm"
                  customInput={<TextField variant="outlined" style={{ width: '100%' }} />}
                />
              )}
            />
            {errors.startDate && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.startDate.message}</div>}
          </FormGroup>

          <FormGroup>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Label style={{ marginBottom: 0 }}>종료일 *</Label>
              <CheckboxContainer style={{ marginBottom: 0 }}>
                <input type="checkbox" checked={isEndDateUndefined} onChange={(e) => setIsEndDateUndefined(e.target.checked)} id="endDateUndefined" />
                <Label htmlFor="endDateUndefined" style={{ marginBottom: 0, fontSize: '13px' }}>
                  미정
                </Label>
              </CheckboxContainer>
            </div>
            <Controller
              control={control}
              name="endDate"
              rules={{ required: !isEndDateUndefined && '종료일은 필수입니다.' }}
              render={({ field }) => (
                <DatePicker
                  locale="ko"
                  selected={isEndDateUndefined ? null : field.value}
                  onChange={(date) => field.onChange(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={5}
                  dateFormat="yyyy-MM-dd HH:mm"
                  disabled={isEndDateUndefined}
                  placeholderText={isEndDateUndefined ? '미정' : ''}
                  customInput={<TextField variant="outlined" style={{ width: '100%' }} />}
                />
              )}
            />
            {errors.endDate && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.endDate.message}</div>}
          </FormGroup>

          {isIntegrated && (
            <FormGroup>
              <Label>솔루션 타입 *</Label>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', marginBottom: '8px', lineHeight: '1.6', textAlign: 'left' }}>
                '전체' 선택 시 모든 솔루션에 노출됩니다.
              </div>
              <Select
                {...register('solType', { required: '솔루션 타입은 필수입니다.' })}
                variant="outlined"
                width="100%"
                wrapperStyle={{ width: '100%', display: 'block' }}
              >
                <option value="ALL">전체</option>
                <option value="COUPANG">쿠팡</option>
                <option value="ST11">11번가</option>
                <option value="ALWAYZ">올웨이즈</option>
                <option value="KAKAO">카카오</option>
                <option value="ESM">ESM+</option>
                <option value="TOSS">토스</option>
              </Select>
              {errors.solType && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.solType.message}</div>}
            </FormGroup>
          )}

          <FormGroup>
            <Label>공지 타입 *</Label>
            <Select
              {...register('noticeType', { required: '공지 타입은 필수입니다.' })}
              variant="outlined"
              width="100%"
              wrapperStyle={{ width: '100%', display: 'block' }}
            >
              <option value={'INFORMATION'}>정보 공지 - 정보 단순 안내</option>
              <option value={'URGENT'}>긴급 공지 - 로그인 차단</option>
            </Select>
            {errors.noticeType && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.noticeType.message}</div>}
          </FormGroup>

          {formData.noticeType === 'URGENT' && (
            <>
              <FormGroup>
                <Label>차단 시작일 *</Label>
                <Controller
                  control={control}
                  name="blockStartDate"
                  rules={{ required: '차단 시작일은 필수입니다.' }}
                  render={({ field }) => (
                    <DatePicker
                      locale="ko"
                      selected={field.value}
                      onChange={(date) => field.onChange(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={5}
                      dateFormat="yyyy-MM-dd HH:mm"
                      customInput={<TextField variant="outlined" style={{ width: '100%' }} />}
                    />
                  )}
                />
                {errors.blockStartDate && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.blockStartDate.message}</div>}
              </FormGroup>

              <FormGroup>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Label style={{ marginBottom: 0 }}>차단 종료일 *</Label>
                  <CheckboxContainer style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={isBlockEndDateUndefined}
                      onChange={(e) => setIsBlockEndDateUndefined(e.target.checked)}
                      id="blockEndDateUndefined"
                    />
                    <Label htmlFor="blockEndDateUndefined" style={{ marginBottom: 0, fontSize: '13px' }}>
                      미정
                    </Label>
                  </CheckboxContainer>
                </div>
                <Controller
                  control={control}
                  name="blockEndDate"
                  rules={{ required: !isBlockEndDateUndefined && '차단 종료일은 필수입니다.' }}
                  render={({ field }) => (
                    <DatePicker
                      locale="ko"
                      selected={isBlockEndDateUndefined ? null : field.value}
                      onChange={(date) => field.onChange(date)}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={5}
                      dateFormat="yyyy-MM-dd HH:mm"
                      disabled={isBlockEndDateUndefined}
                      placeholderText={isBlockEndDateUndefined ? '미정' : ''}
                      customInput={<TextField variant="outlined" style={{ width: '100%' }} />}
                    />
                  )}
                />
                {errors.blockEndDate && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.blockEndDate.message}</div>}
              </FormGroup>
            </>
          )}

          <FormGroup>
            <Label>순서 *</Label>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px', lineHeight: '1.6', textAlign: 'left' }}>
              순서가 낮을 수록 더 먼저 표시됩니다. (단, 순서가 중복되면 나중에 수정된 공지사항이 우선됨)
            </div>
            <Controller
              control={control}
              name="order"
              rules={{
                required: '순서는 필수입니다.',
                min: { value: 1, message: '순서는 1 이상이어야 합니다.' },
              }}
              render={({ field }) => (
                <StyledTextField
                  type="number"
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? null : Number(value));
                  }}
                  placeholder="순서를 입력하세요"
                />
              )}
            />
            {errors.order && <div style={{ color: 'red', fontSize: '12px', textAlign: 'left' }}>{errors.order.message}</div>}
          </FormGroup>

          <Grid display="flex" gap={10} top={20}>
            <Button type="button" color="main" variant="outlined" size="medium" onClick={handleSubmit(handleTempSaveClick)}>
              임시저장
            </Button>
            <Button type="submit" color="main" size="medium">
              {isEditMode ? '수정하기' : '게시하기'}
            </Button>
            {isEditMode && (
              <Button
                type="button"
                color="main"
                variant="outlined"
                size="medium"
                onClick={() => {
                  const currentFormData = watch();
                  MySwal.close();
                  onDelete(notice.noticeId, currentFormData, notice);
                }}
              >
                삭제
              </Button>
            )}
            <Button type="button" color="main" variant="outlined" size="medium" onClick={() => MySwal.close()}>
              취소
            </Button>
          </Grid>
        </form>
      </EditorSection>

      {/* 오른쪽: 미리보기 영역 */}
      <PreviewSection>
        <h3 style={{ marginBottom: '20px' }}>미리보기</h3>
        <NoticePreview
          title={previewData.title}
          subtitle={previewData.subtitle}
          content={previewData.content}
          infoBox={previewData.infoBox}
          noticeType={formData.noticeType}
        />
      </PreviewSection>
    </ModalContainer>
  );
}

/**
 * @alias 미리보기 전용 컴포넌트 (모달 없이 내용만 표시)
 */
function NoticePreview({
  title,
  subtitle,
  content,
  infoBox,
  noticeType,
}: {
  title: string;
  subtitle?: string;
  content: React.ReactNode | React.ReactNode[];
  infoBox?: {
    items: Array<{ label: string; value: string }>;
    warning?: string | string[];
    links?: Array<{ text: string; url: string }>;
  };
  noticeType?: string;
}) {
  return (
    <PreviewContainer>
      {noticeType !== 'URGENT' && (
        <PreviewTopRightBox>
          <PreviewCheckbox type="checkbox" id="previewDontShowAgain" />
          <PreviewLabel htmlFor="previewDontShowAgain">다시는 보지 않기</PreviewLabel>
        </PreviewTopRightBox>
      )}

      <PreviewHeaderSection>
        <PreviewTitle>{title}</PreviewTitle>
        {subtitle && <PreviewSubtitle>{subtitle}</PreviewSubtitle>}
      </PreviewHeaderSection>

      <PreviewContentSection>
        {Array.isArray(content) ? (
          content.map((line, idx) =>
            line === '/n' ? (
              <br key={`content-${idx}`} />
            ) : (
              <PreviewParagraph key={`content-${idx}-${typeof line === 'string' ? line.substring(0, 10) : idx}`}>{line}</PreviewParagraph>
            ),
          )
        ) : (
          <PreviewParagraph>{content}</PreviewParagraph>
        )}

        {infoBox && (
          <PreviewHighlightBox>
            {infoBox.items.length > 0 && (
              <PreviewSpacer>
                {infoBox.items.map((item, index) => (
                  <PreviewHighlightItem key={`item-${index}-${item.label}-${item.value}`}>
                    <PreviewHighlightItemLabel>| {item.label}</PreviewHighlightItemLabel>
                    <PreviewHighlightItemValue>: {item.value}</PreviewHighlightItemValue>
                  </PreviewHighlightItem>
                ))}
                <br />
              </PreviewSpacer>
            )}

            {infoBox.warning && infoBox.warning.length > 0 && (
              <>
                {Array.isArray(infoBox.warning)
                  ? infoBox.warning.map((line, idx) =>
                      line === '/n' ? (
                        <br key={`warning-${idx}`} />
                      ) : (
                        <PreviewSpacer style={{ color: '#003173' }} key={`warning-${idx}-${typeof line === 'string' ? line.substring(0, 15) : idx}`}>
                          {line}
                        </PreviewSpacer>
                      ),
                    )
                  : infoBox.warning && <PreviewSpacer>{infoBox.warning}</PreviewSpacer>}
              </>
            )}

            {infoBox.links && infoBox.links.length > 0 && (
              <>
                {infoBox.links?.map((link, index) => (
                  <PreviewSpacer key={`link-${index}-${link.text}-${link.url}`}>
                    <PreviewLink href={link.url} target="_blank" rel="noopener noreferrer">
                      {link.text}
                    </PreviewLink>
                  </PreviewSpacer>
                ))}
              </>
            )}
          </PreviewHighlightBox>
        )}
      </PreviewContentSection>

      <PreviewFooterSection>
        <PreviewSpacer style={{ display: 'flex', justifyContent: 'center' }}>
          <Button size="medium" width={500} color="main" disabled={true} style={{ cursor: 'default' }}>
            확인
          </Button>
        </PreviewSpacer>
      </PreviewFooterSection>
    </PreviewContainer>
  );
}
